import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { addDoc, collection, getDocs, doc, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import { CompanyModel, CompanyStatus } from '../types/admin';

export { CompanyStatus };


const DB_PATH = 'companies'

export type Company = CompanyModel;


interface CompanyState {
  companies: Company[];
  selectedCompany: Company | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: CompanyState = {
  companies: [],
  selectedCompany: null,
  status: 'idle',
  error: null,
};


export const updateCompany = createAsyncThunk(
  DB_PATH + '/updateCompany',
  async (updatedEntity: Partial<Company> & { id: string }, { rejectWithValue }) => {
    try {
      const { id, ...updates } = updatedEntity;
      const timestamp = Timestamp.now();

      // Update the document in Firestore
      await updateDoc(doc(db, DB_PATH, id), {
        ...updates,
        updatedAt: timestamp,  // Automatically update the updatedAt timestamp
      });

      return { id, ...updates, updatedAt: timestamp };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      } else {
        return rejectWithValue('An unknown error occurred');
      }
    }
  }
);

// Thunk to add a new website to the Firestore database
export const addCompany = createAsyncThunk(
  DB_PATH + '/addCompany',
  async (newEntity: Omit<Company, 'id' | 'createdAt' | 'updatedAt' | 'status'>, { rejectWithValue }) => {
    try {
      // Get the current Firestore Timestamp
      const timestamp = Timestamp.now();

      const companyTimestamps = {
        ...newEntity,
        status: CompanyStatus.CREATED,
        productCount: 0,
        generationQuota: newEntity.generationQuota ?? {
          shopify: { max: null },
          customContent: { max: null },
          brandResearch: { max: null },
          brandKeywordAssignment: { max: null },
        },
        generationUsage: newEntity.generationUsage ?? {
          shopify: 0,
          customContent: 0,
          brandResearch: 0,
          brandKeywordAssignment: 0,
        },
        createdAt: timestamp,
        updatedAt: timestamp,
      }

      const docRef = await addDoc(collection(db, DB_PATH), companyTimestamps);
      return { id: docRef.id, ...companyTimestamps };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      } else {
        return rejectWithValue('An unknown error occurred');
      }
    }
  }
);

export const fetchCompanies = createAsyncThunk(DB_PATH + '/fetchCompanies', async () => {
  const querySnapshot = await getDocs(collection(db, DB_PATH));
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as Company[];
});

const CompanySlice = createSlice({
  name: DB_PATH,
  initialState,
  reducers: {
    setCompanies: (state, action: PayloadAction<Company[]>) => {
      state.companies = action.payload;
    },
    setSelectedCompany: (state, action: PayloadAction<Company | null>) => {
      state.selectedCompany = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanies.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.companies = action.payload;
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      }).addCase(updateCompany.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.companies.findIndex(company => company.id === action.payload.id);
        if (index !== -1) {
          // Update the website in the state
          state.companies[index] = { ...state.companies[index], ...action.payload };
        }
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      });
  },
});

export const { setCompanies, setSelectedCompany } = CompanySlice.actions;
export default CompanySlice.reducer;
